import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { AppBar } from "../../components/AppShell";
import { request } from "../../lib/api";
import { useAuth } from "../../lib/auth";
import { coverFor, fmtDuration, firstName, fmtScore } from "../../lib/helpers";
import type { CourseListItem, CourseProgress, StudentDashboard as DashboardData } from "../../lib/types";

export function StudentDashboard() {
  const { user } = useAuth();

  const stats = useQuery({
    queryKey: ["me", "dashboard"],
    queryFn: () => request<DashboardData>("/me/dashboard"),
  });
  const enrolled = useQuery({
    queryKey: ["me", "enrollments"],
    queryFn: () => request<CourseListItem[]>("/enrollments/me"),
  });

  return (
    <>
      <AppBar crumbs={[{ label: "Tabloul meu" }]} />
      <div className="content">
        <div className="page-head">
          <div>
            <h1>Salut, {user ? firstName(user.full_name) : "…"} <span style={{ fontSize: "1.6rem" }}>👋</span></h1>
            <p>Continuă-ți studiul medical sau explorează cursuri noi.</p>
          </div>
          <div className="page-actions">
            <Link to="/student/catalog" className="btn btn-ghost"><i className="fa-solid fa-plus"></i> Explorează cursuri</Link>
          </div>
        </div>

        <div className="kpi-grid">
          <Kpi label="Cursuri active" value={stats.data?.active_courses ?? "…"} icon="fa-book-open" />
          <Kpi label="Lecții complete" value={stats.data?.lessons_completed ?? "…"} icon="fa-check" />
          <Kpi label="Medie examene" value={fmtScore(stats.data?.exam_average)} icon="fa-chart-simple" />
          <Kpi label="Certificate obținute" value={stats.data?.certificates ?? "…"} icon="fa-award" />
        </div>

        <div className="card-head" style={{ marginTop: 8 }}>
          <h3 style={{ margin: 0, fontSize: "1.15rem" }}>Cursurile mele</h3>
          <Link to="/student/catalog" style={{ fontSize: ".9rem", fontWeight: 600 }}>Explorează catalogul →</Link>
        </div>
        <EnrolledGrid courses={enrolled.data} loading={enrolled.isLoading} />
      </div>
    </>
  );
}

function Kpi({ label, value, icon }: { label: string; value: string | number; icon: string }) {
  return (
    <div className="kpi">
      <div className="kpi-label">{label}</div>
      <div className="kpi-value">{value}</div>
      <div className="kpi-icon"><i className={`fa-solid ${icon}`}></i></div>
    </div>
  );
}

function EnrolledGrid({ courses, loading }: { courses?: CourseListItem[]; loading: boolean }) {
  if (loading) return <div className="card text-muted">Se încarcă cursurile…</div>;
  if (!courses || courses.length === 0) {
    return (
      <div className="card" style={{ textAlign: "center", padding: 48 }}>
        <i className="fa-regular fa-compass" style={{ fontSize: "2rem", color: "var(--c-primary)", marginBottom: 14 }}></i>
        <h3>Nu ești înscris la niciun curs</h3>
        <p>Explorează catalogul pentru a te înscrie la cursuri potrivite rolului tău.</p>
        <Link to="/student/catalog" className="btn btn-primary">Vezi catalogul</Link>
      </div>
    );
  }
  return (
    <div className="grid-3" style={{ marginBottom: 32 }}>
      {courses.map((c) => <CourseCard key={c.id} course={c} />)}
    </div>
  );
}

function CourseCard({ course }: { course: CourseListItem }) {
  const progress = useQuery({
    queryKey: ["me", "course-progress", course.id],
    queryFn: () => request<CourseProgress>(`/me/courses/${course.id}/progress`),
  });
  const p = progress.data;
  const pct = p?.percent ?? 0;
  const isFull = pct >= 100;
  return (
    <article className="course-card">
      <div className="course-cover" style={{ background: coverFor(course) }}>
        <span className="tag">{pct}% complet</span>
        <div>
          <div className="module">{course.category}</div>
          <strong>{course.title}</strong>
        </div>
      </div>
      <div className="course-body">
        <div className="course-meta">
          <span><i className="fa-solid fa-list-check"></i> {p ? `${p.completed_lessons} / ${p.total_lessons} lecții` : "—"}</span>
          <span><i className="fa-regular fa-clock"></i> {fmtDuration(course.duration_minutes)}</span>
        </div>
        <div className="course-progress"><span style={{ width: `${pct}%` }}></span></div>
        <div className="course-footer">
          <span className="pct">{pct}% finalizat</span>
          {isFull
            ? <Link to={`/student/examen/${course.id}`} className="btn btn-accent btn-sm">Examen</Link>
            : <Link to={`/student/curs/${course.id}`} className="btn btn-primary btn-sm">Continuă</Link>}
        </div>
      </div>
    </article>
  );
}
